import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { KeyValuePair } from '../../../../documents/document-filter-item';
import { debounceTime, distinctUntilChanged, finalize, map, publishReplay, refCount, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AdminUsageEQueriesReportFilterItems } from '../admin-usage-e-queries-report-filter-items';
import { CovanceFilter, FilterFieldType, FilterFieldValue } from '../../../../covance-filter';
import { CovanceFilterDateRangeType } from '../../../../covance-filter/filter-type/covance-filter-date-range-type';
import { AdminUsageEQueriesReportFilter } from '../model/admin-usage-e-queries-report-filter';
import { EQueryUsageReportType } from '../e-query-usage-report-type';
import { UsageEQueryReportFilterItemsResolver } from '../usage-e-query-report-filter-items-resolver';

@Component({
    selector: 'admin-usage-e-query-filter',
    template: require('./admin-usage-e-query-filter.component.html'),
    styles: [require('./admin-usage-e-query-filter.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminUsageEQueryFilterComponent implements OnDestroy {
    @Output()
    public startSearch$ = new EventEmitter<AdminUsageEQueriesReportFilter>();
    public formGroup: FormGroup;
    public filterFieldVal: FilterFieldValue = {
        def: {
            queryParam: 'postedDateTime',
            type: FilterFieldType.DateRange
        },
        value: {
            rangeType: 'dateRange'
        }
    };
    public readonly usageEQueryReportTypes =
        [
            EQueryUsageReportType.Protocol,
            EQueryUsageReportType.Sponsor,
            EQueryUsageReportType.Project,
            EQueryUsageReportType.Country,
            EQueryUsageReportType.All,

        ];
    public get label(): string {
        switch (this.usageEQueryReportType.value) {
            case EQueryUsageReportType.Protocol: return 'Protocol Name';
            case EQueryUsageReportType.Project: return 'Project Number';
            case EQueryUsageReportType.Sponsor: return 'Sponsor Name';
            case EQueryUsageReportType.Country: return 'Country Name';
            case EQueryUsageReportType.All: return 'All';
            default: return '';
        }
    }
    public readonly usageEQueryReportType = new FormControl(null, [Validators.required]);
    public readonly usageEQueryReportValue = new FormControl(null, [Validators.required]);
    public readonly searchTerm$ = new Subject<string>();
    public readonly isSearching$ = new BehaviorSubject<boolean>(false);
    public readonly selectedItems$ = new BehaviorSubject<KeyValuePair[]>([]);
    public readonly items$: Observable<KeyValuePair[]>;
    public readonly filterField$: Observable<FilterFieldValue[]>;
    private readonly thresholdSearch = 3;
    private readonly search$ = new Subject();
    private subscription: Subscription;

    constructor(private adminUsageEQueriesReportFilterItems: AdminUsageEQueriesReportFilterItems,
                private dateRangeType: CovanceFilterDateRangeType,
                private usageEQueryReportFilterItemsResolver: UsageEQueryReportFilterItemsResolver,
                private readonly filterService: CovanceFilter) {
        this.formGroup = new FormGroup(
            {
                'reportType' : this.usageEQueryReportType,
                'reportValue' : this.usageEQueryReportValue,
            });
        const filterDefs$ = this.usageEQueryReportType.valueChanges.pipe(
            map(reportType => {
                return this.usageEQueryReportFilterItemsResolver.resolveFilterItems(reportType);
            }),
            publishReplay(1),
            refCount()
        );
        const filterItems$ = filterDefs$.pipe(
            map(filterDefs =>  this.filterService.setFiltersItems([], filterDefs)),
            switchMap(v => {
               return this.adminUsageEQueriesReportFilterItems
                   .getEQueriesReportOptionFilterItems(this.usageEQueryReportValue.valueChanges, this.usageEQueryReportType.value);
            }),
            startWith([]),
            publishReplay(1),
            refCount()
        );
        this.filterField$ = combineLatest(filterDefs$, filterItems$).pipe(
            map(([filterDefs, filterItems]) => {
                this.filterService.setFiltersItems(filterItems, filterDefs);
                return filterDefs;
            }),
            publishReplay(1),
            refCount()
        );

        this.subscription = this.search$.pipe(
           withLatestFrom(this.filterField$)
        ).subscribe(([, filterFields]) => {
            const filter = new AdminUsageEQueriesReportFilter
            (
                this.usageEQueryReportValue.value,
                this.usageEQueryReportType.value,
                filterFields
            );
            this.startSearch$.next(filter);
        });

        this.items$ = this.searchTerm$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => this.isSearching$.next(true)),
            switchMap(term => this.loadItems(term).pipe(
                finalize(() => this.isSearching$.next(false)),
            )),
        );
    }

    public loadItems(term: string): Observable<KeyValuePair[]> {
        if (!term || (this.thresholdSearch && term.length < this.thresholdSearch)) {
            return of([]);
        }
        return this.adminUsageEQueriesReportFilterItems.getEQueriesReportFilterItems(term, this.usageEQueryReportType.value)
            .pipe(
                map(v => {
                    return v ? v.map<KeyValuePair>(p => {
                        return {
                            key: p.key.toString(),
                            value: p.value.toString()
                        };
                    }) : [];
                })
            );
    }

    public onUsageReportChange(usageReportType: string[]) {
        this.usageEQueryReportType.setValue(usageReportType[0]);
        this.usageEQueryReportValue.reset();
        this.filterService.resetFilter();
        this.selectedItems$.next([]);
    }

    public onSelectedItemChange(value: KeyValuePair[]) {
        this.usageEQueryReportValue.setValue(value[0].key);
    }

    public onSearchTermChanged(term: string) {
        this.searchTerm$.next(term);
    }

    public onSearch() {
       this.search$.next();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
